import { Checkbox, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { ButtonStyled } from './CheckboxList.styled';
import { useTickets } from 'contexts/TicketsContext';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';

type DropdownCheckboxListT = {
  shouldFiltersReset: number;
};

export const DropdownCheckboxList: React.FC<DropdownCheckboxListT> = ({ shouldFiltersReset }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [checked, setChecked] = useState<number[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [scheduleCategoryId, setScheduleCategoryId] = useState<number | null>(null);
  const { selectedRoofTop, updateSearchParamsArray } = useTickets();
  const ticketRequestCategoriesResult = useFetchTicketRequestCategories(true, selectedRoofTop);
  const [items, setItems] = useState<TicketRequestCategoryI[]>([]);

  /**
   * Handlers
   */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (categoryId: number) => () => {
    const currentIndex = checked.indexOf(categoryId);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(categoryId);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    const allSelectableIds = items
      .filter(item => item.id !== scheduleCategoryId)
      .map(item => item.id);
    const allSelected = newChecked.length === allSelectableIds.length;
    setIsAllSelected(allSelected);

    updateSearchParamsArray(TicketsRefinementKeysE.Category, newChecked);
    setChecked(newChecked);
  };

  const handleSelectAll = () => {
    const selectableIds = items.filter(item => item.id !== scheduleCategoryId).map(item => item.id);

    if (isAllSelected) {
      setIsAllSelected(false);
      setChecked([]);
      updateSearchParamsArray(TicketsRefinementKeysE.Category, []);
      updateSearchParamsArray(TicketsRefinementKeysE.Schedule, []);
    } else {
      setIsAllSelected(true);
      setChecked(selectableIds);
      updateSearchParamsArray(TicketsRefinementKeysE.Category, selectableIds);
    }
  };

  /**
   * Effects
   */
  useEffect(() => {
    setChecked([]);
    setIsAllSelected(false);
  }, [shouldFiltersReset]);

  useEffect(() => {
    if (ticketRequestCategoriesResult.data) {
      setItems(ticketRequestCategoriesResult.data);
      const scheduleCategory = ticketRequestCategoriesResult.data.find(
        item => item.categoryName.toLowerCase() === 'schedule'
      );
      setScheduleCategoryId(scheduleCategory ? scheduleCategory.id : null);

      // Check if all items are selected
      const searchParams = new URLSearchParams(window.location.search);
      const categoryIds =
        searchParams.get(TicketsRefinementKeysE.Category)?.split(',').map(Number) || [];

      const filteredIds = categoryIds.filter(id => id !== scheduleCategoryId);
      setChecked(filteredIds);

      const allSelectableIds = ticketRequestCategoriesResult.data
        .filter(item => item.id !== scheduleCategoryId)
        .map(item => item.id);

      const allCategoriesSelected =
        filteredIds.length > 0 && allSelectableIds.every(id => filteredIds.includes(id));
      setIsAllSelected(allCategoriesSelected);
    } else {
      setItems([]);
      setChecked([]);
      setIsAllSelected(false);
      setScheduleCategoryId(null);
    }
  }, [ticketRequestCategoriesResult.data, selectedRoofTop, scheduleCategoryId]);

  if (ticketRequestCategoriesResult.isLoading || !selectedRoofTop) {
    return (
      <ButtonStyled variant="outlined" disabled>
        Categories
      </ButtonStyled>
    );
  }

  if (ticketRequestCategoriesResult.error) {
    return <div>Error loading categories</div>;
  }

  return (
    <div>
      <Tooltip
        title={
          isAllSelected
            ? 'All'
            : checked.map(id => items.find(item => item.id === id)?.categoryName).join(', ') ||
              'Select Category'
        }
        placement="top"
      >
        <ButtonStyled
          aria-controls="checkbox-list-menu"
          aria-haspopup="true"
          variant="outlined"
          onClick={handleClick}
          style={{
            maxWidth: '140px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            justifyContent: 'start',
          }}
        >
          {isAllSelected
            ? 'All'
            : checked.map(id => items.find(item => item.id === id)?.categoryName).join(', ') ||
              'Select Category'}
        </ButtonStyled>
      </Tooltip>
      <Menu
        id="checkbox-list-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleSelectAll}>
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={isAllSelected}
              indeterminate={
                checked.length > 0 &&
                checked.length < items.filter(i => i.id !== scheduleCategoryId).length
              }
              tabIndex={-1}
              disableRipple
            />
          </ListItemIcon>
          <ListItemText primary="Select All" />
        </MenuItem>
        {items
          .filter(category => category.id !== scheduleCategoryId)
          .map(category => (
            <MenuItem key={category.id} onClick={handleToggle(category.id)}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={checked.includes(category.id) || isAllSelected}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={category.categoryName} />
            </MenuItem>
          ))}
      </Menu>
    </div>
  );
};

export default DropdownCheckboxList;
